import { AnimatePresence, motion } from 'framer-motion';
import type { CardDefinition, Faction } from '../../types/kotch';
import { CARD_TYPE_INFO } from '../../data/kotch/cards';
import { FACTION_INFO } from '../../data/kotch/factionDecks';
import { getCardArt } from './Card';

interface CardDetailPanelProps {
  card: CardDefinition | null;
  playerFaction?: Faction | null;
}

export default function CardDetailPanel({ card, playerFaction }: CardDetailPanelProps) {
  const cardArt = card ? getCardArt(card, card.faction ? undefined : playerFaction) : null;

  return (
    <div className="kotch-card-detail-panel">
      <AnimatePresence mode="wait">
        {card ? (
          <motion.div
            key={card.id}
            className="kotch-card-detail-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            {/* Card art background */}
            {cardArt && (
              <div
                className="kotch-detail-art"
                style={{ backgroundImage: `url(${cardArt})` }}
              />
            )}

            {/* Info overlay */}
            <div className="kotch-detail-info">
              {/* Name */}
              {card.name && (
                <div className="kotch-detail-name">{card.name}</div>
              )}

              {/* Type + Power */}
              <div className="kotch-detail-type-row">
                <span className={`kotch-detail-type ${card.type}`}>
                  {CARD_TYPE_INFO[card.type].name}
                </span>
                <span className="kotch-detail-power">{card.power}</span>
              </div>

              {/* Faction badge */}
              {card.faction && (
                <div
                  className="kotch-detail-faction"
                  style={{ borderColor: FACTION_INFO[card.faction]?.themeColor }}
                >
                  <span
                    className="kotch-detail-faction-dot"
                    style={{ backgroundColor: FACTION_INFO[card.faction]?.themeColor }}
                  />
                  {card.faction}
                </div>
              )}

              {/* Description */}
              <div className="kotch-detail-description">
                {card.description || CARD_TYPE_INFO[card.type].description}
              </div>

              {/* Combat info */}
              <div className="kotch-detail-combat">
                <div className="kotch-detail-combat-row wins">
                  <span className="kotch-detail-combat-label">Beats</span>
                  <span className="kotch-detail-combat-value">{CARD_TYPE_INFO[card.type].beats}</span>
                </div>
                <div className="kotch-detail-combat-row loses">
                  <span className="kotch-detail-combat-label">Loses to</span>
                  <span className="kotch-detail-combat-value">{CARD_TYPE_INFO[card.type].losesTo}</span>
                </div>
              </div>

              {/* Ability */}
              {card.ability && (
                <div className="kotch-detail-ability">
                  <div className="kotch-detail-ability-name">{card.ability.name}</div>
                  <div className="kotch-detail-ability-trigger">{card.ability.trigger.replace('_', ' ')}</div>
                  <div className="kotch-detail-ability-desc">{card.ability.description}</div>
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="empty"
            className="kotch-card-detail-empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
          >
            Hover a card for details
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
